import { Injectable } from '@angular/core';

import { ValidationClientGroup } from '../../../core';

enum LocalStorageKey {
    ADMINISTRATOR = 'AdministratorRole',
    ADVISORY_MODEL = 'advisoryModel',
    BRANCH_CODE = 'BranchCode',
    CLIENT_CODE = 'ClientCode',
    CLIENT_ID = 'ClientID',
    CLIENT_IS_EDITABLE = 'IsEditable',
    CLIENT_JSON = 'ClientJSON',
    CLIENT_NAMES = 'ClientNames',
    CONTRACT_CODE = 'ContractCode',
    DOMAIN = 'Domain',
    EXPIRATION_DATE = 'ExpirationDate',
    EXPIRED_DOC = 'ExpiredDoc',
    EXPIRED_TEST = 'ExpiredTest',
    IS_PBC = 'isPBC',
    LANGUAGE = 'Language',
    RELATED_CONTRACT_CODE = 'RelatedContractCode',
    SESSION_TOKEN = 'SessionToken',
    STEP = 'Step',
    USER_CODE = 'UserCode',
    ROLNAME = 'RolName',
    VALIDATION_CLIENT_GROUPS = 'ValidationGroups',
    IS_TITULAR = 'IsTitular',
    HIDDEN_OPERATIONS = 'HiddenOperations',
    MEETING_DETAILS = "MeetingDetails",
}

@Injectable()
export class LocalStorage {
    private _administrator = false;
    private _advisoryModel: string = undefined;
    private _branchCode: string = undefined;
    private _contractCode: string = undefined;
    private _clientCode: string = undefined;
    private _clientId: string = undefined;
    private _clientIsEditable = false;
    private _clientJson: string = undefined;
    private _clientNames: string[] = [];
    private _domain: string = undefined;
    private _expirationDate: string = undefined;
    private _expiredDoc = false;
    private _expiredTest = false;
    private _isPBC = false;
    private _language: string = undefined;
    private _relatedContractCode: string = undefined;
    private _sessionToken: string = undefined;
    private _step = 0;
    private _userCode: string = undefined;
    private _rolName: string = undefined;
    private _validationGroups: ValidationClientGroup[] = [];
    private _isTitular: string = undefined;
    private _hiddenOperations;
    private _meetingDetails;




    get administrator(): boolean {
        if (window.localStorage && window.localStorage.getItem(LocalStorageKey.ADMINISTRATOR)) {
            this._administrator = window.localStorage.getItem(LocalStorageKey.ADMINISTRATOR).toLowerCase() === 'yes';
        }

        return this._administrator;
    }

    get advisoryModel(): string {
        if (window.localStorage && window.localStorage.getItem(LocalStorageKey.ADVISORY_MODEL)) {
            this._advisoryModel = window.localStorage.getItem(LocalStorageKey.ADVISORY_MODEL);
        }

        return this._advisoryModel;
    }

    get contractCode(): string {
        if (window.localStorage && window.localStorage.getItem(LocalStorageKey.CONTRACT_CODE)) {
            this._contractCode = window.localStorage.getItem(LocalStorageKey.CONTRACT_CODE);
        }

        return this._contractCode;
    }

    set contractCode(value: string) {
        if (window.localStorage) {
            if (!!value) {
                window.localStorage.setItem(LocalStorageKey.CONTRACT_CODE, value);
            } else if (window.localStorage.getItem(LocalStorageKey.CONTRACT_CODE)) {
                window.localStorage.removeItem(LocalStorageKey.CONTRACT_CODE);
            }
        }

        this._contractCode = value;
    }

    get branchCode(): string {
        if (window.localStorage && window.localStorage.getItem(LocalStorageKey.BRANCH_CODE)) {
            this._branchCode = window.localStorage.getItem(LocalStorageKey.BRANCH_CODE);
        }

        return this._branchCode;
    }

    get clientCode(): string {
        if (window.localStorage && window.localStorage.getItem(LocalStorageKey.CLIENT_CODE)) {
            this._clientCode = window.localStorage.getItem(LocalStorageKey.CLIENT_CODE);
        }

        return this._clientCode;
    }

    set clientCode(value: string) {
        if (window.localStorage) {
            if (!!value) {
                window.localStorage.setItem(LocalStorageKey.CLIENT_CODE, value);
            } else if (window.localStorage.getItem(LocalStorageKey.CLIENT_CODE)) {
                window.localStorage.removeItem(LocalStorageKey.CLIENT_CODE);
            }
        }

        this._clientCode = value;
    }

    get clientId(): string {
        if (window.localStorage && window.localStorage.getItem(LocalStorageKey.CLIENT_ID)) {
            this._clientId = window.localStorage.getItem(LocalStorageKey.CLIENT_ID);
        }

        return this._clientId;
    }

    set clientId(value: string) {
        if (window.localStorage) {
            if (!!value) {
                window.localStorage.setItem(LocalStorageKey.CLIENT_ID, value);
            } else if (window.localStorage.getItem(LocalStorageKey.CLIENT_ID)) {
                window.localStorage.removeItem(LocalStorageKey.CLIENT_ID);
            }
        }

        this._clientId = value;
    }

    get clientIsEditable(): boolean {
        if (window.localStorage && window.localStorage.getItem(LocalStorageKey.CLIENT_IS_EDITABLE)) {
            this._clientIsEditable = window.localStorage.getItem(LocalStorageKey.CLIENT_IS_EDITABLE).toLowerCase() === 'true';
        }

        return this._clientIsEditable;
    }

    get clientJson(): string {
        if (window.localStorage && window.localStorage.getItem(LocalStorageKey.CLIENT_JSON)) {
            this._clientJson = window.localStorage.getItem(LocalStorageKey.CLIENT_JSON);
        }

        return this._clientJson;
    }

    get clientNames(): string[] {
        if (window.localStorage && window.localStorage.getItem(LocalStorageKey.CLIENT_NAMES)) {
            this._clientNames = window.localStorage.getItem(LocalStorageKey.CLIENT_NAMES)?.split(',');
        }

        return this._clientNames;
    }

    get domain(): string | undefined {
        if (window.localStorage && window.localStorage.getItem(LocalStorageKey.DOMAIN)) {
            this._domain = window.localStorage.getItem(LocalStorageKey.DOMAIN);
        }

        return this._domain;
    }

    get expirationDate(): string {
        if (window.localStorage && window.localStorage.getItem(LocalStorageKey.EXPIRATION_DATE)) {
            this._expirationDate = window.localStorage.getItem(LocalStorageKey.EXPIRATION_DATE);
        }

        return this._expirationDate;
    }

    get isExpiredDoc(): boolean {
        if (window.localStorage && window.localStorage.getItem(LocalStorageKey.EXPIRED_DOC)) {
            this._expiredDoc = window.localStorage.getItem(LocalStorageKey.EXPIRED_DOC).toLowerCase() === 'true';
        }

        return this._expiredDoc;
    }

    get isExpiredTest(): boolean {
        if (window.localStorage && window.localStorage.getItem(LocalStorageKey.EXPIRED_TEST)) {
            this._expiredTest = window.localStorage.getItem(LocalStorageKey.EXPIRED_TEST).toLowerCase() === 'true';
        }

        return this._expiredTest;
    }

    get isPBC(): boolean | undefined {
        if (window.localStorage && window.localStorage.getItem(LocalStorageKey.IS_PBC)) {
            this._isPBC = window.localStorage.getItem(LocalStorageKey.IS_PBC).toLowerCase() === 'true';
        }

        return this._isPBC;
    }

    get language(): string | undefined {
        if (window.localStorage && window.localStorage.getItem(LocalStorageKey.LANGUAGE)) {
            this._language = window.localStorage.getItem(LocalStorageKey.LANGUAGE);
        }

        return this._language;
    }

    get relatedContractCode(): string {
        if (window.localStorage && window.localStorage.getItem(LocalStorageKey.RELATED_CONTRACT_CODE)) {
            this._relatedContractCode = window.localStorage.getItem(LocalStorageKey.RELATED_CONTRACT_CODE);
        }

        return this._relatedContractCode;
    }

    get sessionToken(): string | undefined {
        if (window.localStorage && window.localStorage.getItem(LocalStorageKey.SESSION_TOKEN)) {
            this._sessionToken = window.localStorage.getItem(LocalStorageKey.SESSION_TOKEN);
        }

        return this._sessionToken;
    }

    set sessionToken(value: string) {
        this._sessionToken = value;
    }

    get step(): number {
        if (window.sessionStorage && window.sessionStorage.getItem(LocalStorageKey.STEP)) {
            this._step = Number(window.sessionStorage.getItem(LocalStorageKey.STEP));
        }

        return this._step;
    }

    set step(value: number) {
        if (window.sessionStorage) {
            if (!!value) {
                window.sessionStorage.setItem(LocalStorageKey.STEP, String(value));
            } else if (window.sessionStorage.getItem(LocalStorageKey.STEP)) {
                window.sessionStorage.removeItem(LocalStorageKey.STEP);
            }
        }

        this._step = value;
    }

    get userCode(): string | undefined {
        if (window.localStorage && window.localStorage.getItem(LocalStorageKey.USER_CODE)) {
            this._userCode = window.localStorage.getItem(LocalStorageKey.USER_CODE);
        }

        return this._userCode;
    }

    get rolName(): string | undefined {
        if (window.localStorage && window.localStorage.getItem(LocalStorageKey.ROLNAME)) {
            this._rolName = window.localStorage.getItem(LocalStorageKey.ROLNAME);
        }

        return this._rolName;
    }

    get isTitular(): string | undefined {
        if (window.localStorage && window.localStorage.getItem(LocalStorageKey.IS_TITULAR)) {
            this._isTitular = window.localStorage.getItem(LocalStorageKey.IS_TITULAR);
        }

        return this._isTitular;
    }

    get hiddenOperations(): boolean {
        if (window.localStorage && window.localStorage.getItem(LocalStorageKey.HIDDEN_OPERATIONS)) {
            this._hiddenOperations = window.localStorage.getItem(LocalStorageKey.HIDDEN_OPERATIONS);
        }

        return this._hiddenOperations;
    }

    get meetingDetails(): boolean {
        if (window.localStorage && window.localStorage.getItem(LocalStorageKey.MEETING_DETAILS)) {
            this._meetingDetails = window.localStorage.getItem(LocalStorageKey.MEETING_DETAILS);
        }

        return this.meetingDetails;
    }

    get validationClientGroups(): ValidationClientGroup[] {
        if (window.localStorage && window.localStorage.getItem(LocalStorageKey.VALIDATION_CLIENT_GROUPS)) {
            this._validationGroups = [];

            const validationClientGroups = window.localStorage.getItem(LocalStorageKey.VALIDATION_CLIENT_GROUPS).split(',');
            validationClientGroups.map((group) => {
                switch (group) {
                    case 'D':
                        this._validationGroups.push(ValidationClientGroup.PERSONAL_DATA);
                        break;
                    case 'P':
                        this._validationGroups.push(ValidationClientGroup.PROFESSIONAL_DATA);
                        break;
                    case 'F':
                        this._validationGroups.push(ValidationClientGroup.PUBLIC_DATA);
                        break;
                    default:
                        break;
                }
            });
        }

        return this._validationGroups;
    }

    write() {
        if (window.localStorage) {
            if (this._administrator !== undefined) {
                window.localStorage.setItem(LocalStorageKey.ADMINISTRATOR, this._administrator ? 'true' : 'false');
            }
            if (this._branchCode) {
                window.localStorage.setItem(LocalStorageKey.BRANCH_CODE, this._branchCode);
            }
            if (this._contractCode) {
                window.localStorage.setItem(LocalStorageKey.CONTRACT_CODE, this._contractCode);
            }
            if (this._clientCode) {
                window.localStorage.setItem(LocalStorageKey.CLIENT_CODE, this._clientCode);
            }
            if (this._clientId) {
                window.localStorage.setItem(LocalStorageKey.CLIENT_ID, this._clientId);
            }
            if (this._clientIsEditable !== undefined) {
                window.localStorage.setItem(LocalStorageKey.CLIENT_IS_EDITABLE, this._clientIsEditable ? 'true' : 'false');
            }
            if (this._domain) {
                window.localStorage.setItem(LocalStorageKey.DOMAIN, this._domain);
            }
            if (this._isPBC !== undefined) {
                window.localStorage.setItem(LocalStorageKey.IS_PBC, this._isPBC ? 'true' : 'false');
            }
            if (this._language) {
                window.localStorage.setItem(LocalStorageKey.LANGUAGE, this._language);
            }
            if (this._relatedContractCode) {
                window.localStorage.setItem(LocalStorageKey.RELATED_CONTRACT_CODE, this._relatedContractCode);
            }
            if (this._sessionToken) {
                window.localStorage.setItem(LocalStorageKey.SESSION_TOKEN, this._sessionToken);
            }
            if (this._step) {
                window.sessionStorage.setItem(LocalStorageKey.STEP, String(this._step));
            }
            if (this._userCode) {
                window.localStorage.setItem(LocalStorageKey.USER_CODE, this._userCode);
            }
            if (this._rolName) {
                window.localStorage.setItem(LocalStorageKey.ROLNAME, this._rolName);
            }
            if (this._isTitular) {
                window.localStorage.setItem(LocalStorageKey.IS_TITULAR, this._isTitular);
            }
            if (this._hiddenOperations) {
                window.localStorage.setItem(LocalStorageKey.HIDDEN_OPERATIONS, this._hiddenOperations);
            }
            if (this._meetingDetails) {
                window.localStorage.setItem(LocalStorageKey.MEETING_DETAILS, String(this._meetingDetails));
            }
            if (this._advisoryModel) {
                window.localStorage.setItem(LocalStorageKey.ADVISORY_MODEL, String(this._advisoryModel));
            }

        }
        if(window.sessionStorage){
            if (this._step) {
                window.sessionStorage.setItem(LocalStorageKey.STEP, String(this._step));
            }
        }
    }

    clear() {
        if (window.localStorage) {
            window.localStorage.clear();
        }
        if(window.sessionStorage){
            window.sessionStorage.clear();
        }
    }
}
